import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useI18n } from '../context/I18nContext';

export default function CustomSelect({
  id,
  name,
  label,
  options = [],
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled = false,
  searchable = true,
  className = '',
  labelClassName = '',
  triggerClassName = '',
  dropdownClassName = '',
}) {
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPlacement, setDropdownPlacement] = useState('bottom');
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(240);
  const [dropdownCoords, setDropdownCoords] = useState({ left: 0, top: 0, width: 0 });
  const resolvedPlaceholder = placeholder || t('components.customSelect.placeholder', 'Select an option');
  const resolvedSearchPlaceholder = searchPlaceholder || t('components.customSelect.searchPlaceholder', 'Search...');
  const resolvedEmptyText = emptyText || t('components.customSelect.emptyText', 'No results found');

  const normalizedOptions = useMemo(
    () =>
      options.map((option) => {
        if (option && typeof option === 'object') {
          return {
            label: String(option.searchLabel ?? option.label ?? option.value ?? ''),
            displayLabel: option.displayLabel ?? option.label ?? option.value ?? '',
            value: option.value,
            disabled: Boolean(option.disabled),
          };
        }
        return {
          label: String(option ?? ''),
          displayLabel: option,
          value: option,
          disabled: false,
        };
      }),
    [options],
  );

  const selectedOption = normalizedOptions.find((option) => option.value === value);
  const filteredOptions = normalizedOptions.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  function closeMenu() {
    const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
    if (activeElement && containerRef.current?.contains(activeElement) && typeof activeElement.blur === 'function') {
      activeElement.blur();
    }
    setIsOpen(false);
  }

  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current || typeof window === 'undefined') return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const gap = 10;
    const minimumPanelHeight = searchable ? 220 : 160;
    const preferredPanelHeight = searchable ? 320 : 260;
    const spaceBelow = Math.max(0, viewportHeight - rect.bottom - gap);
    const spaceAbove = Math.max(0, rect.top - gap);
    const shouldOpenTop = spaceBelow < minimumPanelHeight && spaceAbove > spaceBelow;
    const availableSpace = shouldOpenTop ? spaceAbove : spaceBelow;

    const width = rect.width;
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;

    const left = rect.left - containerRect.left;
    const panelHeight = Math.min(preferredPanelHeight, availableSpace);
    const top = shouldOpenTop
      ? rect.top - containerRect.top - panelHeight - gap
      : rect.bottom - containerRect.top + gap;

    setDropdownPlacement(shouldOpenTop ? 'top' : 'bottom');
    setDropdownMaxHeight(Math.max(160, Math.min(preferredPanelHeight, availableSpace)));
    setDropdownCoords({ left, top, width });
  }, [searchable]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeMenu();
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') {
        closeMenu();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);
    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      return;
    }
    if (searchable) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [isOpen, searchable]);

  return (
    <div ref={containerRef} className={`relative ${className || 'w-full'}`}>
      {label ? (
        <label
          htmlFor={id}
          className={
            labelClassName ||
            'mb-1.5 ml-1 block text-sm font-medium text-[var(--dash-text-soft)]'
          }
        >
          {label}
        </label>
      ) : null}

      {name ? <input type="hidden" id={id} name={name} value={value ?? ''} readOnly /> : null}

      <button
        id={id}
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (disabled) return;
          setIsOpen((prev) => {
            const next = !prev;
            if (next) {
              // Position the panel synchronously so it opens at the trigger,
              // not from the (0,0) viewport origin.
              updateDropdownPosition();
            }
            return next;
          });
        }}
        disabled={disabled}
        className={`relative flex h-10 w-full items-center justify-between rounded-lg border border-[var(--dash-border-strong)] bg-[var(--dash-surface)] px-3 text-left text-sm outline-none transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dash-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgba(2,6,23,0.94)] ${
          isOpen ? 'border-[var(--dash-border)] shadow-[0_0_0_1px_var(--dash-border)]' : 'hover:border-[var(--dash-border)]'
        } disabled:cursor-not-allowed disabled:opacity-60 ${triggerClassName}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        >
        <span className={`truncate ${selectedOption ? 'text-[var(--dash-text)]' : 'text-[var(--dash-text-muted)]'}`}>
          {selectedOption ? selectedOption.displayLabel : resolvedPlaceholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-[var(--dash-text-muted)] transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {!disabled ? (
        <div
          className={`absolute z-[1000] overflow-hidden rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)] shadow-2xl shadow-black/10 transition-opacity duration-150 ease-out ${
            isOpen
              ? 'visible opacity-100'
              : 'pointer-events-none invisible opacity-0'
          } ${dropdownClassName}`}
          style={{
            left: dropdownCoords.left,
            top: dropdownCoords.top,
            width: dropdownCoords.width,
          }}
        >
          {searchable ? (
            <div className="border-b border-[var(--dash-border)] p-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--dash-text-muted)]" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={resolvedSearchPlaceholder}
                  className="w-full rounded-lg border border-[var(--dash-border-strong)] bg-[var(--dash-surface-2)] py-2 pl-9 pr-3 text-sm text-[var(--dash-text)] outline-none transition-all focus:border-[var(--dash-primary)] focus:ring-2 focus:ring-[var(--dash-primary)] focus:ring-offset-2 focus:ring-offset-[rgba(2,6,23,0.94)]"
                />
              </div>
            </div>
          ) : null}

          <div className="overflow-y-auto p-1" style={{ maxHeight: Math.max(96, dropdownMaxHeight - (searchable ? 58 : 0)) }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={String(option.value)}
                  type="button"
                  disabled={option.disabled}
                  onClick={() => {
                    if (option.disabled) return;
                    onChange?.(option.value);
                    closeMenu();
                  }}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${
                    value === option.value
                      ? 'bg-[var(--dash-primary)]/15 font-medium text-[var(--dash-primary)]'
                      : 'text-[var(--dash-text-soft)] hover:bg-[var(--dash-surface-2)]'
                  } ${option.disabled ? 'cursor-not-allowed opacity-40' : ''}`}
                >
                  <span className="truncate">{option.displayLabel}</span>
                  {value === option.value ? <Check className="h-4 w-4" /> : null}
                </button>
              ))
            ) : (
              <p className="px-3 py-4 text-center text-sm text-[var(--dash-text-muted)]">{resolvedEmptyText}</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
